// Thin LOCAL fetch client for the doctor profile extras (avatar + KPIs). These fields are additive on
// /v1/doctor/profile and aren't in the published DoctorClient schema yet (it would strip them on parse),
// so we read them raw here — deliberately NOT in the published SDK (local-first, mirrors the other
// doctor thin clients). baseUrl + getToken → Bearer, credentials:"include" (dev cookie), { data } unwrap.
import { useMemo } from "react";
import { API_ORIGIN } from "../config";
import { useAppAuth } from "../auth";

export interface ProfileKpis {
  patients: number;
  visits_this_year: number;
}

// Doctor-set rich profile fields (THU-340) — patient-facing, non-clinical. Editable here; surfaced on
// the patient doctor card. Not in the published schema, so read/written via this thin client.
export interface DoctorRichProfile {
  bio: string | null;
  years_experience: number | null;
  education: string | null;
  languages: string[];
  focus_areas: string[];
}

export interface ProfileExtras {
  avatar_url: string | null;
  kpis: ProfileKpis;
  rich: DoctorRichProfile;
}

// Full PUT /v1/doctor/profile body — base credential fields (from the published ProfileUpdate) plus
// the additive rich fields. Sent as one request (the endpoint replaces the whole profile).
export type ProfileSaveBody = Record<string, unknown> & {
  bio?: string | null;
  years_experience?: number | null;
  education?: string | null;
  languages?: string[];
  focus_areas?: string[];
};

export interface ProfileExtrasApi {
  get(): Promise<ProfileExtras>;
  uploadAvatar(image: string): Promise<string>;
  saveProfile(body: ProfileSaveBody): Promise<void>;
}

export function useProfileExtrasApi(): ProfileExtrasApi {
  const { getToken } = useAppAuth();
  return useMemo<ProfileExtrasApi>(() => {
    async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
      const headers: Record<string, string> = {};
      if (body !== undefined) headers["Content-Type"] = "application/json";
      const token = await getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API_ORIGIN}${path}`, {
        method,
        headers,
        credentials: "include",
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const text = await res.text();
      const json: unknown = text ? JSON.parse(text) : {};
      if (!res.ok) {
        const err = (json as { error?: { message?: string } }).error;
        throw new Error(err?.message ?? "Request failed");
      }
      return (json as { data?: T }).data as T;
    }
    type ProfileResp = {
      avatar_url?: string | null;
      kpis?: ProfileKpis;
      profile?: {
        avatar_url?: string | null;
        bio?: string | null;
        years_experience?: number | null;
        education?: string | null;
        languages?: string[] | null;
        focus_areas?: string[] | null;
      } | null;
    };
    return {
      get: () =>
        request<ProfileResp>("GET", "/v1/doctor/profile").then((d) => {
          const p = d?.profile ?? {};
          return {
            avatar_url: p.avatar_url ?? d?.avatar_url ?? null,
            kpis: { patients: d?.kpis?.patients ?? 0, visits_this_year: d?.kpis?.visits_this_year ?? 0 },
            rich: {
              bio: p.bio ?? null,
              years_experience: p.years_experience ?? null,
              education: p.education ?? null,
              languages: p.languages ?? [],
              focus_areas: p.focus_areas ?? [],
            },
          };
        }),
      uploadAvatar: (image) =>
        request<{ avatar_url: string }>("POST", "/v1/doctor/avatar", { image }).then((d) => d.avatar_url),
      saveProfile: (body) => request<unknown>("PUT", "/v1/doctor/profile", body).then(() => undefined),
    };
  }, [getToken]);
}
